import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { anaApi, apiError, getHijoActivo, mediaUrl } from "../../lib/api";
import { renderRich } from "../../lib/richText";
import { AnaChart } from "./AnaChart";

const Avatar = ({ foto, size = "w-14 h-14" }) => (
  foto ? <img src={mediaUrl(foto)} alt="Ana" className={`${size} rounded-full object-cover ring-2 ring-[var(--gold)]`} data-testid="ana-avatar" />
    : <span className={`${size} rounded-full bg-[var(--gold)] text-[#0A1C30] font-extrabold grid place-items-center text-xl`} data-testid="ana-avatar">A</span>
);

const Bubble = ({ m, foto }) => (
  <div className="space-y-3">
    <div className="flex justify-end"><div className="max-w-[85%] rounded-2xl rounded-br-md bg-[var(--gold)] text-[#0A1C30] px-4 py-2.5 text-sm font-semibold" data-testid="ana-msg-user">{m.pregunta}</div></div>
    <div className="flex gap-2.5 items-start">
      <Avatar foto={foto} size="w-8 h-8" />
      <div className="flex-1 min-w-0 space-y-3">
        <div className="rounded-2xl rounded-tl-md bg-white text-[#2A3446] px-4 py-3 text-sm blog-rich" data-testid="ana-msg-ana" dangerouslySetInnerHTML={{ __html: renderRich(m.respuesta) }} />
        {m.charts?.length > 0 && <div className="grid sm:grid-cols-2 gap-3 items-start">{m.charts.map((c, i) => <AnaChart key={i} c={c} i={i} />)}</div>}
      </div>
    </div>
  </div>
);

export const AnaPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [hijo, setHijo] = useState("");
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    anaApi.config().then((c) => {
      setCfg(c);
      if (c.es_padre && c.hijos.length) setHijo(c.hijos.some((h) => h.id === getHijoActivo()) ? getHijoActivo() : c.hijos[0].id);
    }).catch(() => setCfg({ disponible: false }));
  }, []);
  useEffect(() => { setMsgs([]); }, [hijo]);
  useEffect(() => { if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs.length, busy]);

  if (!cfg?.disponible) return null;
  const ask = async (text) => {
    const t = (text ?? q).trim();
    if (!t || busy) return;
    setBusy(true);
    try { const r = await anaApi.preguntar(t, hijo, msgs.length === 0); setMsgs((m) => [...m, r]); setQ(""); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <section className="rounded-3xl bg-[#0A1C30] text-white overflow-hidden ring-1 ring-white/10 relative" data-testid="ana-panel">
      <div className="absolute inset-0 overflow-hidden pointer-events-none"><div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[var(--gold)]/15 blur-3xl" /></div>
      <div className="relative p-5 sm:p-6 flex gap-4 items-start" data-testid="ana-aviso">
        <Avatar foto={cfg.foto_url} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-extrabold tracking-[0.25em] text-[var(--gold)] flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> ANA · COORDINADORA ACADÉMICA</div>
          <h2 className="mt-1 text-lg sm:text-xl font-extrabold leading-snug">{cfg.aviso_titulo}</h2>
          <p className="mt-1 text-sm text-white/70">{cfg.aviso_texto}</p>
        </div>
      </div>
      <div className="relative bg-[#F4F6FA] text-[#0A1C30] p-4 sm:p-5">
        {cfg.es_padre && (
          cfg.hijos.length ? (
            <label className="flex flex-wrap items-center gap-2 text-xs font-bold mb-3">Consultar el desarrollo de:
              <select data-testid="ana-hijo-select" value={hijo} onChange={(e) => setHijo(e.target.value)} className="rounded-lg ring-1 ring-black/10 bg-white px-3 py-1.5 text-sm">
                {cfg.hijos.map((h) => <option key={h.id} value={h.id}>{h.nombre}{h.grupo ? ` · Grupo ${h.grupo}` : ""}</option>)}
              </select>
            </label>
          ) : <div className="text-sm text-[#6B7688] mb-3" data-testid="ana-no-hijos">Aún no tienes hijos/as vinculados. Pide al equipo Supera que los vincule.</div>
        )}
        <div ref={boxRef} className="max-h-[520px] overflow-y-auto space-y-5 pr-1" data-testid="ana-messages">
          {msgs.length === 0 && <div className="text-sm text-[#6B7688] flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1E9E5A]" /> Conversación privada: Ana solo analiza {cfg.es_padre ? "el perfil de tu hijo/a seleccionado" : "tu perfil"}. Al salir de la app, esta conversación se borra de tu pantalla.</div>}
          {msgs.map((m) => <Bubble key={m.id} m={m} foto={cfg.foto_url} />)}
          {busy && <div className="flex items-center gap-2 text-sm text-[#6B7688]" data-testid="ana-typing"><Loader2 className="w-4 h-4 animate-spin" /> Ana está revisando el perfil…</div>}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {cfg.sugerencias.map((s, i) => (
            <button key={s} data-testid={`ana-sugerencia-${i}`} disabled={busy || (cfg.es_padre && !hijo)} onClick={() => ask(s)}
              className="text-xs font-bold rounded-full bg-white ring-1 ring-black/10 hover:ring-[var(--gold)] px-3 py-1.5 transition-colors disabled:opacity-50">{s}</button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mt-3 flex gap-2">
          <input data-testid="ana-input" value={q} onChange={(e) => setQ(e.target.value)} maxLength={500} disabled={cfg.es_padre && !hijo}
            placeholder="Pregúntale a Ana sobre tu desarrollo…" className="flex-1 min-w-0 rounded-xl bg-white ring-1 ring-black/10 px-4 py-3 text-sm outline-none focus:ring-[var(--gold)]" />
          <button data-testid="ana-send" disabled={busy || !q.trim()} className="rounded-xl bg-[#0A1C30] text-white px-4 disabled:opacity-40 hover:brightness-125 transition"><Send className="w-4 h-4" /></button>
        </form>
      </div>
    </section>
  );
};
